import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/CrudModule";
import { CaseDrawer } from "@/components/CaseDrawer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, ListChecks, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";

const BUCKETS = [
  ["new_unassigned", "New / Unassigned"],
  ["assigned_untouched", "Assigned · Untouched"],
  ["overdue_followups", "Overdue Follow-Ups"],
  ["todays_appointments", "Today’s Appointments"],
  ["apps_missing_info", "Applications · Missing Info"],
  ["ready_for_review", "Ready for Review"],
  ["ready_to_submit", "Ready to Submit"],
  ["outstanding_requirements", "Outstanding Requirements"],
  ["underwriting_delayed", "Underwriting · Delayed"],
  ["approved_unplaced", "Approved · Unplaced"],
  ["servicing_issues", "Servicing Issues"],
  ["my_tasks", "My Tasks"],
];

const PRIORITY_STYLE = { high: "bg-red-100 text-red-700", medium: "bg-amber-100 text-amber-700", normal: "bg-slate-100 text-slate-600" };

export default function WorkQueue() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const canManage = ["admin", "ops_manager"].includes(user.role);
  const [data, setData] = useState(null);
  const [active, setActive] = useState("new_unassigned");
  const [openCase, setOpenCase] = useState(null);
  const [filters, setFilters] = useState({ owner: "", carrier: "", lead_source: "", priority: "" });

  const load = useCallback(() => {
    setData(null);
    const params = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
    api.get(`/workflow/queue${params ? `?${params}` : ""}`).then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  }, [filters]);
  useEffect(load, [load]);

  useEffect(() => {
    if (!data?.counts || (data.counts[active] || 0) > 0) return;
    const firstWithWork = BUCKETS.find(([key]) => (data.counts[key] || 0) > 0);
    if (firstWithWork) setActive(firstWithWork[0]);
  }, [data, active]);

  const rows = data?.buckets?.[active] || [];

  return (
    <div className="space-y-5" data-testid="work-queue">
      <PageHeader title={t("queue.title")} subtitle={t("queue.subtitle")}>
        <Button variant="outline" size="sm" onClick={load} data-testid="wq-refresh"><RefreshCw className="mr-1.5 h-4 w-4" /> {t("common.refresh")}</Button>
      </PageHeader>

      <div className="flex flex-wrap gap-2 rounded-lg border bg-card p-3">
        <Input className="h-8 w-40" placeholder={t("queue.filter_owner")} value={filters.owner}
          onChange={(e) => setFilters({ ...filters, owner: e.target.value })} data-testid="wq-filter-owner" />
        <Input className="h-8 w-40" placeholder={t("queue.filter_carrier")} value={filters.carrier}
          onChange={(e) => setFilters({ ...filters, carrier: e.target.value })} data-testid="wq-filter-carrier" />
        <Input className="h-8 w-40" placeholder={t("queue.filter_source")} value={filters.lead_source}
          onChange={(e) => setFilters({ ...filters, lead_source: e.target.value })} data-testid="wq-filter-source" />
        <select className="h-8 rounded-md border px-2 text-sm" value={filters.priority}
          onChange={(e) => setFilters({ ...filters, priority: e.target.value })} data-testid="wq-filter-priority">
          <option value="">{t("queue.all_priorities")}</option><option value="high">{t("priority.high")}</option>
          <option value="medium">{t("priority.medium")}</option><option value="normal">{t("priority.normal")}</option>
        </select>
      </div>

      {!data ? <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        : data.error ? <div className="text-sm text-muted-foreground">Unable to load the work queue.</div> : (
          <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
            <div className="space-y-1" data-testid="wq-buckets">
              {BUCKETS.map(([key, label]) => (
                <button key={key} onClick={() => setActive(key)} data-testid={`wq-bucket-${key}`}
                  className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors ${active === key ? "bg-[#17324D] text-white" : "hover:bg-muted"}`}>
                  <span className="flex items-center gap-2"><ListChecks className="h-3.5 w-3.5" /> {t(`queue.buckets.${key}`, { defaultValue: label })}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${active === key ? "bg-white/20" : "bg-muted"}`}>
                    {data.counts?.[key] ?? 0}
                  </span>
                </button>
              ))}
            </div>

            <div className="rounded-lg border bg-card p-3" data-testid="wq-list">
              {rows.length === 0 ? <div className="py-10 text-center text-sm text-muted-foreground">{t("common.empty")}</div> : (
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2">{t("queue.client_case")}</th><th>{t("common.stage")}</th><th>{t("common.assigned")}</th><th>{t("common.due")}</th><th>{t("common.priority")}</th></tr></thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r.lead_id || r.followup_id || i} className="cursor-pointer border-b hover:bg-muted/40"
                        onClick={() => r.lead_id && setOpenCase(r.lead_id)} data-testid={`wq-row-${r.lead_id || r.followup_id || i}`}>
                        <td className="py-2">{r.client_name}
                          <div className="text-[11px] text-muted-foreground">{r.case_ref || r.display_id || ""}</div></td>
                        <td className="text-xs">{r.canonical ? t(`workflow.${r.kind}.${r.canonical}`, { defaultValue: r.canonical }) : (r.type || "—")}</td>
                        <td className="text-xs">{r.assigned_agent || r.assigned_to || "—"}</td>
                        <td className="text-xs">{r.next_action_date || r.due_date || "—"}</td>
                        <td><span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${PRIORITY_STYLE[r.priority] || PRIORITY_STYLE.normal}`}>{t(`priority.${r.priority || "normal"}`, { defaultValue: r.priority || "normal" })}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

      {openCase && <CaseDrawer leadId={openCase} onClose={() => { setOpenCase(null); load(); }} canManage={canManage} />}
    </div>
  );
}
